import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_ASSET_CONFIG, normaliseAssetConfig, resolveAssetImageSrc } from '../utils/assetTypes';
import { useConfirm } from '../components/ConfirmDialog';
import PipelineStageEditor from '../components/PipelineStageEditor';

function AssetConfigSettings() {
  const { authFetch, user } = useAuth();
  const { confirmElement, requestConfirm } = useConfirm();
  const [config, setConfig] = useState(DEFAULT_ASSET_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedAssetType, setSelectedAssetType] = useState(null);
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [newAsset, setNewAsset] = useState({ value: '', label: '', summary: '' });
  // Sales rework Phase 2: relational stage lists + phases + T&C sets
  const [stagesByType, setStagesByType] = useState({});
  const [phases, setPhases] = useState([]);
  const [termsSets, setTermsSets] = useState([]);

  // Gareth 2026-11-03: managers add and configure products too (the API's
  // PRODUCT_CONFIG_EDIT), so everyone who has this tab can edit it.
  const canEdit = ['admin', 'manager'].includes(user?.role);

  const fetchStages = useCallback(async () => {
    try {
      const [stagesRes, phasesRes] = await Promise.all([
        authFetch('/api/v1/admin/pipeline-stages'),
        authFetch('/api/v1/admin/pipeline-phases')
      ]);
      if (stagesRes.ok) {
        const data = await stagesRes.json();
        setStagesByType(data.data || {});
      }
      if (phasesRes.ok) {
        const data = await phasesRes.json();
        setPhases(data.data || []);
      }
    } catch (error) {
      // Stage data is supplementary; the config page stays usable without it.
    }
  }, [authFetch]);

  const fetchTermsSets = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/document-library?kind=terms_and_conditions&active=true');
      if (!response.ok) return;
      const data = await response.json();
      setTermsSets([...new Set((data.data || []).map(doc => doc.set_key))]);
    } catch (error) {
      // Optional data — dropdown just shows no sets.
    }
  }, [authFetch]);

  useEffect(() => {
    fetchStages();
    fetchTermsSets();
  }, [fetchStages, fetchTermsSets]);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/v1/admin/asset-config');
      if (!response.ok) throw new Error('Failed to load asset configuration');
      const data = await response.json();
      const normalised = normaliseAssetConfig(data.data);
      setConfig(normalised);
      // Keep the open product. Reading selectedAssetType here made every tab
      // click reload the config and throw away unsaved edits.
      setSelectedAssetType(current => current || normalised.asset_types[0]?.value || null);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveConfig = async (nextConfig = config) => {
    try {
      setSaving(true);
      setMessage('');
      const response = await authFetch('/api/v1/admin/asset-config', {
        method: 'PUT',
        body: JSON.stringify(nextConfig)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save asset configuration');
      }
      const data = await response.json();
      setConfig(normaliseAssetConfig(data.data));
      setMessage('Product settings saved');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateAssetType = (value, changes) => {
    setConfig(prev => ({
      ...prev,
      asset_types: prev.asset_types.map(type => (
        type.value === value ? { ...type, ...changes } : type
      ))
    }));
  };

  const updateAssetTypeWear = (value, key, rawValue) => {
    const numeric = rawValue === '' ? '' : parseInt(rawValue, 10);
    setConfig(prev => ({
      ...prev,
      asset_types: prev.asset_types.map(type => (
        type.value === value
          ? { ...type, wear: { ...(type.wear || {}), [key]: numeric } }
          : type
      ))
    }));
  };

  const updatePricingSchedule = (value, nextSchedule) => {
    updateAssetType(value, { pricing_schedule: nextSchedule });
  };

  const scheduleEntryChange = (type, index, changes) => {
    const schedule = [...(type.pricing_schedule || [])];
    schedule[index] = { ...schedule[index], ...changes };
    updatePricingSchedule(type.value, schedule);
  };

  const addScheduleEntry = (type) => {
    const schedule = [...(type.pricing_schedule || [])];
    schedule.push({
      key: `entry_${Date.now()}`,
      label: '',
      amount_minor_units: 0,
      recurring: false,
      pipeline_stage_ref: null
    });
    updatePricingSchedule(type.value, schedule);
  };

  const removeScheduleEntry = (type, index) => {
    const schedule = (type.pricing_schedule || []).filter((_, i) => i !== index);
    updatePricingSchedule(type.value, schedule);
  };

  const updateDefault = (key, rawValue, isFloat = false) => {
    const numeric = rawValue === '' ? '' : (isFloat ? parseFloat(rawValue) : parseInt(rawValue, 10));
    setConfig(prev => ({
      ...prev,
      defaults: { ...(prev.defaults || {}), [key]: numeric }
    }));
  };

  const addAssetType = () => {
    if (!newAsset.value || !newAsset.label) {
      setMessage('Value and Label are required');
      return;
    }
    const safeValue = newAsset.value.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (config.asset_types.some(t => t.value === safeValue)) {
      setMessage('A product type with this value already exists');
      return;
    }
    const newType = {
      value: safeValue,
      label: newAsset.label,
      summary: newAsset.summary || '',
      image: '/asset-types/asset-default.svg',
      active: true,
      default_service_interval_months: config.defaults?.service_interval_months || 12,
      warranty_months: config.defaults?.warranty_months || 12,
      wear: {}
    };
    const updated = { ...config, asset_types: [...config.asset_types, newType] };
    setConfig(updated);
    setSelectedAssetType(safeValue);
    setShowAddAssetModal(false);
    setNewAsset({ value: '', label: '', summary: '' });
    saveConfig(updated);
  };

  const deleteAssetType = (value) => {
    requestConfirm({
      title: 'Delete product type?',
      message: `Are you sure you want to delete the "${value}" product type?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => performDeleteAssetType(value)
    });
  };

  const performDeleteAssetType = (value) => {
    const updated = {
      ...config,
      asset_types: config.asset_types.filter(t => t.value !== value)
    };
    setConfig(updated);
    if (selectedAssetType === value) {
      setSelectedAssetType(updated.asset_types[0]?.value || null);
    }
    saveConfig(updated);
  };

  const getSelectedType = () => config.asset_types.find(t => t.value === selectedAssetType);

  const handleImageUpload = async (typeValue, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setSaving(true);
        setMessage('');
        const response = await authFetch(`/api/v1/admin/asset-config/asset-types/${typeValue}/image`, {
          method: 'POST',
          body: JSON.stringify({ image: reader.result })
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to upload image');
        }
        const data = await response.json();
        setConfig(normaliseAssetConfig(data.data));
        setMessage('Product type image updated');
      } catch (error) {
        setMessage(error.message);
      } finally {
        setSaving(false);
      }
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return <div className="admin-loading">Loading product settings...</div>;
  }

  const selectedType = getSelectedType();

  return (
    <div className="admin-section asset-config-section">
      {confirmElement}
      <div className="section-header">
        <div>
          <h2>Product Configuration</h2>
          <p className="section-desc">Configure product types, default pricing, pipeline stages, service defaults, and wear intervals.</p>
        </div>
        <div className="section-actions">
          {canEdit && (
            <>
              <button className="btn-secondary" onClick={() => setShowAddAssetModal(true)}>
                + Add Product Type
              </button>
              <button className="btn-primary" onClick={() => saveConfig()} disabled={saving}>
                {saving ? 'Saving...' : 'Save All Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {message && <div className="admin-info">{message}</div>}

      <div className="defaults-section">
        <h3>Global Defaults</h3>
        <p className="section-desc">These values apply to all new assets and calendar task durations.</p>
        <div className="form-row compact">
          <div className="form-group">
            <label>Annual Service Duration (hours)</label>
            <input
              type="number"
              min="0.5"
              max="24"
              step="0.5"
              value={config.defaults?.annual_service_duration_hours || ''}
              disabled={!canEdit}
              onChange={(e) => updateDefault('annual_service_duration_hours', e.target.value, true)}
            />
          </div>
          <div className="form-group">
            <label>Quarterly Inspection Duration (hours)</label>
            <input
              type="number"
              min="0.5"
              max="24"
              step="0.5"
              value={config.defaults?.health_check_duration_hours || ''}
              disabled={!canEdit}
              onChange={(e) => updateDefault('health_check_duration_hours', e.target.value, true)}
            />
          </div>
        </div>
      </div>

      <div className="asset-type-tabs">
        {config.asset_types.map(type => (
          <button
            key={type.value}
            className={`asset-type-tab ${selectedAssetType === type.value ? 'active' : ''} ${type.active === false ? 'inactive' : ''}`}
            onClick={() => setSelectedAssetType(type.value)}
          >
            <img src={resolveAssetImageSrc(type.image)} alt="" />
            <span>{type.label}</span>
          </button>
        ))}
      </div>

      {selectedType && (
        <div className="asset-type-detail">
          <div className="asset-type-header">
            <div className="asset-type-header-image">
              <img src={resolveAssetImageSrc(selectedType.image)} alt={selectedType.label} />
              {canEdit && (
                <label className="image-upload-button">
                  Change Image
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                    onChange={(e) => handleImageUpload(selectedType.value, e.target.files?.[0])}
                  />
                </label>
              )}
            </div>
            <div className="asset-type-header-fields">
              <div className="form-row compact">
                <div className="form-group">
                  <label>System Value</label>
                  <input value={selectedType.value} disabled />
                </div>
                <div className="form-group">
                  <label>Display Label</label>
                  <input
                    value={selectedType.label || ''}
                    disabled={!canEdit}
                    onChange={(e) => updateAssetType(selectedType.value, { label: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Summary / Description</label>
                <input
                  value={selectedType.summary || ''}
                  disabled={!canEdit}
                  onChange={(e) => updateAssetType(selectedType.value, { summary: e.target.value })}
                />
              </div>
              <div className="form-row compact">
                <div className="form-group">
                  <label>Default Service (months)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={selectedType.default_service_interval_months || ''}
                    disabled={!canEdit}
                    onChange={(e) => updateAssetType(selectedType.value, { default_service_interval_months: parseInt(e.target.value, 10) || '' })}
                  />
                </div>
                <div className="form-group">
                  <label>Warranty (months)</label>
                  <input
                    type="number"
                    min="0"
                    max="120"
                    value={selectedType.warranty_months || ''}
                    disabled={!canEdit}
                    onChange={(e) => updateAssetType(selectedType.value, { warranty_months: parseInt(e.target.value, 10) || '' })}
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label config-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedType.active !== false}
                      disabled={!canEdit}
                      onChange={(e) => updateAssetType(selectedType.value, { active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div className="form-row compact">
                <div className="form-group">
                  <label>Maintenance model</label>
                  <select
                    value={selectedType.maintenance_model || 'cadence'}
                    disabled={!canEdit}
                    onChange={(e) => updateAssetType(selectedType.value, { maintenance_model: e.target.value })}
                  >
                    <option value="robot_plan">Robot plan (quarterly over contract)</option>
                    <option value="cadence">Cadence (configured per asset)</option>
                    <option value="none">None (no scheduled maintenance)</option>
                  </select>
                </div>
                {(selectedType.maintenance_model || 'cadence') === 'robot_plan' && (
                  <div className="form-group">
                    <label>Contract length (months)</label>
                    <input
                      type="number"
                      min="3"
                      max="240"
                      value={selectedType.contract_months || ''}
                      placeholder="60"
                      disabled={!canEdit}
                      onChange={(e) => updateAssetType(selectedType.value, { contract_months: parseInt(e.target.value, 10) || '' })}
                    />
                  </div>
                )}
              </div>

              {/* Service cover moved to the ASSET record (2026-07-23): cover
                  is a commercial fact about one machine's contract, set as a
                  designation (Service / Repair & Replace) with an end date on
                  the asset page. Warranty stays a product fact above. */}

              <div className="config-subheading">Default pricing schedule (GBP — safety-net baseline)</div>
              <p className="pricing-schedule-hint">
                Priced stages: amounts due at defined points. One-off entries feed the installation
                table on quotes; monthly entries feed the rental table. Distributor pricing on the
                distributor record still overrides this per deal.
              </p>
              <table className="pricing-schedule-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Amount (£)</th>
                    <th>Type</th>
                    <th>Due at stage</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedType.pricing_schedule || []).map((entry, index) => (
                    <tr key={entry.key || index}>
                      <td>
                        <input
                          value={entry.label || ''}
                          placeholder="e.g. Deposit"
                          disabled={!canEdit}
                          onChange={(e) => scheduleEntryChange(selectedType, index, { label: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          type="number" min="0" step="0.01"
                          value={entry.amount_minor_units != null ? (entry.amount_minor_units / 100) : ''}
                          disabled={!canEdit}
                          onChange={(e) => scheduleEntryChange(selectedType, index, {
                            amount_minor_units: e.target.value === '' ? 0 : Math.round(parseFloat(e.target.value) * 100)
                          })}
                        />
                      </td>
                      <td>
                        <select
                          value={entry.recurring ? 'recurring' : 'one_off'}
                          disabled={!canEdit}
                          onChange={(e) => scheduleEntryChange(selectedType, index, { recurring: e.target.value === 'recurring' })}
                        >
                          <option value="one_off">One-off</option>
                          <option value="recurring">Monthly</option>
                        </select>
                      </td>
                      <td>
                        <select
                          value={entry.pipeline_stage_ref || ''}
                          disabled={!canEdit}
                          onChange={(e) => scheduleEntryChange(selectedType, index, { pipeline_stage_ref: e.target.value || null })}
                        >
                          <option value="">—</option>
                          {(stagesByType[selectedType.value] || []).map(stage => (
                            <option key={stage.stage_ref} value={stage.stage_ref}>{stage.stage_name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {canEdit && (
                          <button className="btn-danger btn-small" onClick={() => removeScheduleEntry(selectedType, index)}>×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="form-row compact pricing-schedule-actions">
                {canEdit && (
                  <button className="btn-secondary btn-small" onClick={() => addScheduleEntry(selectedType)}>
                    + Add Priced Stage
                  </button>
                )}
                <label className="checkbox-label config-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedType.price_is_placeholder !== false}
                    disabled={!canEdit}
                    onChange={(e) => updateAssetType(selectedType.value, { price_is_placeholder: e.target.checked })}
                  />
                  Placeholder (needs sign-off)
                </label>
              </div>

              <div className="config-subheading">Sales behaviour</div>
              <div className="form-row compact">
                <div className="form-group">
                  <label className="checkbox-label config-checkbox" title="Create a blank asset when a line item reaches its Won stage">
                    <input
                      type="checkbox"
                      checked={selectedType.generates_asset === true}
                      disabled={!canEdit}
                      onChange={(e) => updateAssetType(selectedType.value, { generates_asset: e.target.checked })}
                    />
                    Generates asset at Won
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label config-checkbox" title="Generate an asset completion form for installation/commissioning">
                    <input
                      type="checkbox"
                      checked={selectedType.asset_form_enabled === true}
                      disabled={!canEdit}
                      onChange={(e) => updateAssetType(selectedType.value, { asset_form_enabled: e.target.checked })}
                    />
                    Asset completion form
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label config-checkbox" title="Line items can be flagged for the Contracts Meeting">
                    <input
                      type="checkbox"
                      checked={selectedType.rag_eligible === true}
                      disabled={!canEdit}
                      onChange={(e) => updateAssetType(selectedType.value, { rag_eligible: e.target.checked })}
                    />
                    Contracts Meeting eligible
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label config-checkbox" title="Sellable as a line item but never offered as an asset type">
                    <input
                      type="checkbox"
                      checked={selectedType.sellable_only === true}
                      disabled={!canEdit}
                      onChange={(e) => updateAssetType(selectedType.value, { sellable_only: e.target.checked })}
                    />
                    Sellable only (no asset)
                  </label>
                </div>
              </div>
              <div className="form-row compact">
                <div className="form-group">
                  <label>Terms &amp; Conditions set</label>
                  <select
                    value={selectedType.terms_set_key || ''}
                    disabled={!canEdit}
                    onChange={(e) => updateAssetType(selectedType.value, { terms_set_key: e.target.value || null })}
                  >
                    <option value="">None</option>
                    {termsSets.map(setKey => (
                      <option key={setKey} value={setKey}>{setKey.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <small className="field-hint">Sets are managed in Settings → Documents; the regional variant is picked at pack generation.</small>
                </div>
              </div>

              <div className="form-row compact">
                <div className="form-group">
                  <label>Brush Days</label>
                  <input
                    type="number"
                    min="0"
                    value={selectedType.wear?.brush_days || ''}
                    disabled={!canEdit}
                    onChange={(e) => updateAssetTypeWear(selectedType.value, 'brush_days', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Nozzle Days</label>
                  <input
                    type="number"
                    min="0"
                    value={selectedType.wear?.nozzle_days || ''}
                    disabled={!canEdit}
                    onChange={(e) => updateAssetTypeWear(selectedType.value, 'nozzle_days', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Belt Days</label>
                  <input
                    type="number"
                    min="0"
                    value={selectedType.wear?.drive_belt_days || ''}
                    disabled={!canEdit}
                    onChange={(e) => updateAssetTypeWear(selectedType.value, 'drive_belt_days', e.target.value)}
                  />
                </div>
              </div>
              {canEdit && (
                <button className="btn-danger btn-small" onClick={() => deleteAssetType(selectedType.value)}>
                  Delete Product Type
                </button>
              )}
            </div>
          </div>

          <PipelineStageEditor
            productType={selectedType.value}
            stages={stagesByType[selectedType.value] || []}
            phases={phases}
            canEdit={canEdit}
            authFetch={authFetch}
            onChanged={fetchStages}
          />
        </div>
      )}

      {showAddAssetModal && (
        <div className="modal-backdrop" onClick={() => setShowAddAssetModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Product Type</h2>
              <button className="modal-close" onClick={() => setShowAddAssetModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>System Value *</label>
                <input
                  type="text"
                  value={newAsset.value}
                  onChange={(e) => setNewAsset({ ...newAsset, value: e.target.value })}
                  placeholder="e.g. new_product (lowercase, underscores)"
                />
              </div>
              <div className="form-group">
                <label>Display Label *</label>
                <input
                  type="text"
                  value={newAsset.label}
                  onChange={(e) => setNewAsset({ ...newAsset, label: e.target.value })}
                  placeholder="e.g. New Product"
                />
              </div>
              <div className="form-group">
                <label>Summary</label>
                <input
                  type="text"
                  value={newAsset.summary}
                  onChange={(e) => setNewAsset({ ...newAsset, summary: e.target.value })}
                  placeholder="Brief description of the product type"
                />
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowAddAssetModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={addAssetType}>Add Product Type</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssetConfigSettings;
